/**
 * useEnrichment — detail-panel enrichment pipeline.
 *
 * Fans out parallel fetches to Wikipedia, Wikidata, Commons, Europeana
 * and Lokalhistoriewiki (plus curated.ts overrides) to build the rich
 * sidebar shown when a pin is opened. Results are memoised in an
 * in-memory LRU of ~100 entries to keep re-opens instant.
 *
 * Consumers: App.tsx wires the returned `enrichment` into the per-layer
 * Detail* components (heritage, runestone, folklore, etc.).
 */
import { useState, useCallback, useEffect } from "react";
import type { MutableRefObject } from "react";
import type { MapRef } from "react-map-gl/maplibre";

// Types
import type {
  LayerType, HeritageSite, FolkloreRecord, RuneStone, HistoricalEvent, Monument,
  ProtectedBuilding, Museum, UnescoSite,
  BarrowSite, MegalithSite, PlaqueSite, StolpersteinSite,
  EnrichmentData, EuropeanaItem, NearbyPinItem, RecentlyViewedItem, Lang, Country,
} from "../types/index.ts";

/** Europeana fetcher attaches sideband totals to the array — typed view of that. */
type EuropeanaResults = EuropeanaItem[] & { _total?: number; _searchUrl?: string };

// Utils
import { COUNTRY_WIKI_LANG } from "../constants/index.ts";
import { haversineKm } from "../utils/geo.ts";
import { sanitizeImageUrl, isRelevantImageUrl } from "../utils/images.ts";

// API — enrichment is dynamic-imported. The 87 kB enrichment.ts module
// is the largest single source file in the app; pulling it into the main
// chunk just to wire up these 19 fetchers added ~50 kB gzip to every page
// load. Now it ships as its own chunk that's loaded the first time a
// detail panel opens (i.e. the first time useEnrichment's effect runs
// with a non-null `selected`). Subsequent panel opens use the cached
// promise — there's only ever one network round per session.
import type * as EnrichmentMod from "../api/enrichment.ts";
let _enrichmentMod: Promise<typeof EnrichmentMod> | null = null;
const loadEnrichment = (): Promise<typeof EnrichmentMod> => (_enrichmentMod ??= import("../api/enrichment.ts"));
const fetchCommonsNearby: typeof EnrichmentMod.fetchCommonsNearby = (...a) => loadEnrichment().then(m => m.fetchCommonsNearby(...a));
const fetchGeographPhotos: typeof EnrichmentMod.fetchGeographPhotos = (...a) => loadEnrichment().then(m => m.fetchGeographPhotos(...a));
const fetchWikipediaDirectly: typeof EnrichmentMod.fetchWikipediaDirectly = (...a) => loadEnrichment().then(m => m.fetchWikipediaDirectly(...a));
const searchWikipediaRunestone: typeof EnrichmentMod.searchWikipediaRunestone = (...a) => loadEnrichment().then(m => m.searchWikipediaRunestone(...a));
const fetchLokalhistoriewiki: typeof EnrichmentMod.fetchLokalhistoriewiki = (...a) => loadEnrichment().then(m => m.fetchLokalhistoriewiki(...a));
const fetchLexDk: typeof EnrichmentMod.fetchLexDk = (...a) => loadEnrichment().then(m => m.fetchLexDk(...a));
const fetchTrapDk: typeof EnrichmentMod.fetchTrapDk = (...a) => loadEnrichment().then(m => m.fetchTrapDk(...a));
const fetchEuropeana: typeof EnrichmentMod.fetchEuropeana = (...a) => loadEnrichment().then(m => m.fetchEuropeana(...a));
const fetchSMKCollection: typeof EnrichmentMod.fetchSMKCollection = (...a) => loadEnrichment().then(m => m.fetchSMKCollection(...a));
const fetchDigitaltMuseum: typeof EnrichmentMod.fetchDigitaltMuseum = (...a) => loadEnrichment().then(m => m.fetchDigitaltMuseum(...a));
const fetchDanishDetail: typeof EnrichmentMod.fetchDanishDetail = (...a) => loadEnrichment().then(m => m.fetchDanishDetail(...a));
const fetchDanishBuildingDetail: typeof EnrichmentMod.fetchDanishBuildingDetail = (...a) => loadEnrichment().then(m => m.fetchDanishBuildingDetail(...a));
const fetchSwedishDetail: typeof EnrichmentMod.fetchSwedishDetail = (...a) => loadEnrichment().then(m => m.fetchSwedishDetail(...a));
const fetchFinnishDetail: typeof EnrichmentMod.fetchFinnishDetail = (...a) => loadEnrichment().then(m => m.fetchFinnishDetail(...a));
const fetchDutchDetail: typeof EnrichmentMod.fetchDutchDetail = (...a) => loadEnrichment().then(m => m.fetchDutchDetail(...a));
const fetchNiedersachsenDetail: typeof EnrichmentMod.fetchNiedersachsenDetail = (...a) => loadEnrichment().then(m => m.fetchNiedersachsenDetail(...a));
const fetchWikidataHeritage: typeof EnrichmentMod.fetchWikidataHeritage = (...a) => loadEnrichment().then(m => m.fetchWikidataHeritage(...a));
const fetchWikipediaTitleImage: typeof EnrichmentMod.fetchWikipediaTitleImage = (...a) => loadEnrichment().then(m => m.fetchWikipediaTitleImage(...a));
const fetchWikidataImageById: typeof EnrichmentMod.fetchWikidataImageById = (...a) => loadEnrichment().then(m => m.fetchWikidataImageById(...a));
const fetchEnrichment: typeof EnrichmentMod.fetchEnrichment = (...a) => loadEnrichment().then(m => m.fetchEnrichment(...a));
const fetchCommonsCategoryImages: typeof EnrichmentMod.fetchCommonsCategoryImages = (...a) => loadEnrichment().then(m => m.fetchCommonsCategoryImages(...a));
const fetchEuropeanaEntity: typeof EnrichmentMod.fetchEuropeanaEntity = (...a) => loadEnrichment().then(m => m.fetchEuropeanaEntity(...a));
const fetchWikidataShipDetail: typeof EnrichmentMod.fetchWikidataShipDetail = (...a) => loadEnrichment().then(m => m.fetchWikidataShipDetail(...a));

// API — pre-generated enrichment cache
import { getPregeneratedEnrichment } from "../api/pregenerated.ts";

// API — curated
import { loadBattlefieldCurated, loadRunestoneCurated, loadHeritageArt, loadSiteNarratives, getAtmosphericArt } from "../api/curated.ts";

// API — rock art (SHFA images)
// fetchSHFAImages is dynamic-imported inside the rock-art branch — keeps
// the rockart.ts module (now lazy in useFetchLayers too) out of main.

// API — museum collections
import { fetchMuseumCollections } from "../api/museumCollections.ts";

// API — Heritage at Risk
import { checkHeritageAtRisk } from "../api/heritageAtRisk.ts";

// API — knowledge graph
import { fetchWikidataRelations } from "../api/graph.ts";

// Supabase client (for community content fetches)
import { supabase } from "../lib/supabase.ts";

// ============================================================
// TEMPORAL NEIGHBORS — extract approximate year from period/date strings
// ============================================================
const PERIOD_YEAR_MAP: Record<string, number> = {
  // Danish periods
  Oldtid: -2000, Stenalder: -3000, Bronzealder: -1200, Jernalder: 200,
  Vikingetid: 900, Middelalder: 1200, "Historisk Tid": 1600, Udateret: 0,
  // Swedish periods (Fornsök)
  stenålder: -3000, bronsålder: -1200, järnålder: 200, vikingatid: 900,
  medeltid: 1200, "historisk tid": 1600,
  // Norwegian periods (Askeladden)
  steinalder: -3000, bronsealder: -1200, jernalder: 200, vikingtid: 900,
  middelalder: 1200,
  // Finnish periods (Museovirasto)
  kivikautinen: -3000, pronssikautinen: -1200, rautakautinen: 200,
  varhaismetallikautinen: -1000, keskiaikainen: 1200, historiallinen: 1600,
  esihistoriallinen: -2000,
  // English
  "stone age": -3000, "bronze age": -1200, "iron age": 200,
  "viking age": 900, "medieval": 1200, "middle ages": 1200,
};

function extractApproxYear(dateStr: string | undefined): number | null {
  if (!dateStr) return null;
  const s = dateStr.trim();
  // Direct lookup in period map (case-insensitive)
  const lower = s.toLowerCase();
  for (const [key, yr] of Object.entries(PERIOD_YEAR_MAP)) {
    if (lower === key.toLowerCase() || lower.includes(key.toLowerCase())) return yr;
  }
  // ISO date: "1389-06-20" or "1389-01-01"
  const isoMatch = s.match(/^(-?\d{3,4})-\d{2}/);
  if (isoMatch) return parseInt(isoMatch[1], 10);
  // Plain year: "1200", "800", "-500"
  const yearMatch = s.match(/^(-?\d{3,4})$/);
  if (yearMatch) return parseInt(yearMatch[1], 10);
  // Swedish "-talet" suffix: "1200-talet" → 1200
  const taletMatch = s.match(/(\d{3,4})-?talet/i);
  if (taletMatch) return parseInt(taletMatch[1], 10);
  // Century patterns: "12th century", "12. århundrede"
  const centuryMatch = s.match(/(\d{1,2})\.\s*(?:century|århundrede|århundre|århundrade|vuosisata)/i);
  if (centuryMatch) return (parseInt(centuryMatch[1], 10) - 1) * 100 + 50;
  const centuryEnMatch = s.match(/(\d{1,2})(?:st|nd|rd|th)\s+century/i);
  if (centuryEnMatch) return (parseInt(centuryEnMatch[1], 10) - 1) * 100 + 50;
  // Extract first 3-4 digit number from string
  const numMatch = s.match(/(\d{3,4})/);
  if (numMatch) return parseInt(numMatch[1], 10);
  return null;
}

// ============================================================
// HOOK INTERFACE
// ============================================================

export interface UseEnrichmentParams {
  // `selected` is the layer-typed pin currently shown in the detail panel.
  // The body of useEnrichment switches on `selectedType` and casts to the
  // matching site interface, so a loose type avoids forcing every caller
  // to upcast manually.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selected: any;
  selectedType: LayerType;
  lang: Lang;
  enrichmentCache: MutableRefObject<Map<string, EnrichmentData>>;
  heritageFeatures: GeoJSON.Feature[];
  mapRef: MutableRefObject<MapRef | null>;
  folkloreIndex: MutableRefObject<Map<string, FolkloreRecord>>;
  battlefieldIndex: MutableRefObject<Map<string, HistoricalEvent>>;
  monumentIndex: MutableRefObject<Map<string, Monument>>;
  runestoneIndex: MutableRefObject<Map<string, RuneStone>>;
  barrowIndex: MutableRefObject<Map<string, BarrowSite>>;
  megalithIndex: MutableRefObject<Map<string, MegalithSite>>;
  addRecentlyViewed: (item: RecentlyViewedItem) => void;
}

export interface UseEnrichmentReturn {
  enrichment: EnrichmentData | null;
  enrichLoading: boolean;
  setEnrichment: React.Dispatch<React.SetStateAction<EnrichmentData | null>>;
  setEnrichLoading: React.Dispatch<React.SetStateAction<boolean>>;
  cacheEnrichment: (id: string, data: EnrichmentData) => void;
}

// ============================================================
// HOOK IMPLEMENTATION
// ============================================================

export function useEnrichment({
  selected,
  selectedType,
  lang,
  enrichmentCache,
  heritageFeatures,
  mapRef,
  folkloreIndex,
  battlefieldIndex,
  monumentIndex,
  runestoneIndex,
  barrowIndex,
  megalithIndex,
  addRecentlyViewed,
}: UseEnrichmentParams): UseEnrichmentReturn {
  const [enrichment, setEnrichment] = useState<EnrichmentData | null>(null);
  const [enrichLoading, setEnrichLoading] = useState(false);

  const cacheEnrichment = useCallback((id: string, data: EnrichmentData) => {
    const cache = enrichmentCache.current;
    // Delete-before-set so refreshing an existing key (pregen entry later
    // replaced by the network result) moves it to the back of the eviction
    // order instead of keeping the stale slot.
    cache.delete(id);
    cache.set(id, data);
    // Eviction: keep max 100 entries (insertion-ordered Map ≈ LRU-by-write)
    if (cache.size > 100) {
      const oldest = cache.keys().next().value;
      if (oldest) cache.delete(oldest);
    }
  }, []);

  // Fetch community photos for a pin (approved only)
  async function fetchCommunityPhotos(pinId: string): Promise<{url: string; credit?: string}[]> {
    if (!supabase) return [];
    const { data } = await supabase
      .from("pin_images")
      .select("image_url, caption, uploaded_by")
      .eq("pin_id", pinId)
      .eq("status", "approved")
      .order("created_at", { ascending: false })
      .limit(10);
    if (!data) return [];
    interface PinImageRow { image_url: string; caption?: string; uploaded_by?: string }
    return (data as PinImageRow[]).map((d) => ({ url: d.image_url, credit: d.caption || "Community photo" }));
  }

  // Fetch best approved community description for a pin
  async function fetchCommunityDescription(pinId: string): Promise<string | undefined> {
    if (!supabase) return undefined;
    const { data } = await supabase
      .from("contributions")
      .select("content")
      .eq("pin_id", pinId)
      .eq("field_name", "description")
      .eq("status", "approved")
      .order("created_at", { ascending: false })
      .limit(1);
    return data?.[0]?.content;
  }

  // Merge community content into enrichment data (photos + description
  // fallback). Mutates `d` in place and returns whether anything was added —
  // callers must trigger a re-render (setEnrichment({...d})) when it did,
  // otherwise the merged photos never reach the screen.
  async function mergeCommunityContent(pinId: string, d: EnrichmentData): Promise<boolean> {
    try {
      const [communityPhotos, communityDesc] = await Promise.allSettled([
        fetchCommunityPhotos(pinId),
        (!d.wikiExtract && !d.description) ? fetchCommunityDescription(pinId) : Promise.resolve(undefined),
      ]);
      let changed = false;
      if (communityPhotos.status === "fulfilled" && communityPhotos.value.length > 0) {
        d.images = [...(d.images ?? []), ...communityPhotos.value];
        changed = true;
      }
      if (communityDesc.status === "fulfilled" && communityDesc.value && !d.wikiExtract && !d.description) {
        d.communityDescription = communityDesc.value;
        changed = true;
      }
      return changed;
    } catch { return false; }
  }

  useEffect(() => {
    let cancelled = false;
    setEnrichment(null);
    if (!selected) return;
    // Track recently viewed
    const rvId = Array.isArray(selected) ? selected[0]?.id : selected.id;
    const rvName = Array.isArray(selected) ? selected[0]?.title : selected.name;
    const rvCountry = Array.isArray(selected) ? selected[0]?.country : selected.country;
    const rvLng = Array.isArray(selected) ? selected[0]?.lng : selected.lng;
    const rvLat = Array.isArray(selected) ? selected[0]?.lat : selected.lat;
    if (rvId && rvName) {
      addRecentlyViewed({
        id: rvId,
        name: rvName,
        layerType: selectedType,
        country: (rvCountry || "DK") as Country,
        timestamp: new Date().toISOString(),
        lng: rvLng,
        lat: rvLat,
      });
    }
    // Check cache first: in-memory LRU → pre-generated static cache → network.
    // Include layer type so two pins of different types at the same coords can't
    // share a cache entry (e.g. a museum and a heritage site at the same address).
    // Use the array-aware rvId/rvLat/rvLng — folklore selections are ARRAYS of
    // records, so `selected.id` is undefined and every folklore pin would share
    // the cache key "folklore:undefined,undefined".
    const cacheKey = `${selectedType}:${rvId ?? `${rvLat},${rvLng}`}`;
    const cached = enrichmentCache.current.get(cacheKey);
    if (cached) { setEnrichment(cached); return; }
    // Check pre-generated enrichment cache (static JSON loaded lazily)
    const pregenId = selected.id || selected.wikidataId;
    if (pregenId) {
      const pregen = getPregeneratedEnrichment(pregenId);
      if (pregen && Object.keys(pregen).length > 0) {
        cacheEnrichment(cacheKey, pregen as EnrichmentData);
        setEnrichment(pregen as EnrichmentData);
        // Show pre-generated data instantly but still run enrichment in background
        // for community content, Europeana, museum finds, etc.
      }
    }

    // Temporal neighbors helper: finds pins from a similar time period within the viewport
    const addTemporalNeighbors = (data: Partial<EnrichmentData>, periodStr: string | undefined, pinLat: number, pinLng: number, pinId: string) => {
      const year = extractApproxYear(periodStr);
      if (year === null || year === 0) return; // skip undated
      const TOLERANCE = 100; // ±100 years
      const bounds = mapRef.current?.getMap().getBounds();
      const candidates: NearbyPinItem[] = [];
      const seen = new Set<string>();
      seen.add(pinId);
      const tryAdd = (id: string, name: string, lat: number, lng: number, layerType: LayerType, country?: Country, periodValue?: string | undefined) => {
        if (seen.has(id)) return;
        // Viewport check
        if (bounds && (lng < bounds.getWest() || lng > bounds.getEast() || lat < bounds.getSouth() || lat > bounds.getNorth())) return;
        const otherYear = extractApproxYear(periodValue);
        if (otherYear === null || otherYear === 0) return;
        if (Math.abs(otherYear - year) > TOLERANCE) return;
        seen.add(id);
        candidates.push({ id, name: name || "?", layerType, distance: haversineKm(pinLat, pinLng, lat, lng), lng, lat, country: country as Country });
      };
      // Scan heritage features (GeoJSON, no index map)
      for (const f of heritageFeatures) {
        if (f.geometry.type !== "Point" || !f.properties) continue;
        const [fLng, fLat] = (f.geometry as GeoJSON.Point).coordinates;
        tryAdd(f.properties.id as string, f.properties.name as string, fLat, fLng, "heritage", f.properties.country as Country, f.properties.period as string);
      }
      // Scan battlefields
      battlefieldIndex.current.forEach(b => tryAdd(b.id, b.name, b.lat, b.lng, "battlefield", b.country, b.date));
      // Scan monuments
      monumentIndex.current.forEach(m => tryAdd(m.id, m.name, m.lat, m.lng, "monument", m.country, m.startDate));
      // Scan runestones (all are Viking Age ~900)
      runestoneIndex.current.forEach(r => tryAdd(r.id, r.name, r.lat, r.lng, "runestone", r.country, "Vikingetid"));
      // Scan barrows
      barrowIndex.current.forEach(b => tryAdd(b.id, b.name, b.lat, b.lng, "barrow", b.country, b.period));
      // Scan megaliths (typically Stone Age/Bronze Age)
      megalithIndex.current.forEach(m => tryAdd(m.id, m.name, m.lat, m.lng, "megalith", m.country, m.period ?? "Stenalder"));
      if (candidates.length > 0) {
        candidates.sort((a, b) => a.distance - b.distance);
        data.temporalNeighbors = candidates.slice(0, 5);
      }
    };

    // Similar sites helper: finds sites of the same type within the same country
    const addSimilarSites = (data: Partial<EnrichmentData>, layerType: LayerType, pinId: string, pinLat: number, pinLng: number, pinCountry: Country | undefined, siteType: string | undefined) => {
      if (!siteType || !pinCountry) return;
      const candidates: (NearbyPinItem & { reason?: string })[] = [];
      const seen = new Set<string>();
      seen.add(pinId);
      const normalizeType = (t: string) => t.toLowerCase().replace(/[_\-\s]+/g, " ").trim();
      const targetType = normalizeType(siteType);
      const tryAdd = (id: string, name: string, lat: number, lng: number, lt: LayerType, country: Country | undefined, itemType: string | undefined) => {
        if (seen.has(id) || !itemType || country !== pinCountry) return;
        if (normalizeType(itemType) !== targetType) return;
        seen.add(id);
        candidates.push({ id, name: name || "?", layerType: lt, distance: haversineKm(pinLat, pinLng, lat, lng), lng, lat, country: country as Country });
      };
      // Scan the matching index based on layer type
      if (layerType === "heritage") {
        for (const f of heritageFeatures) {
          if (f.geometry.type !== "Point" || !f.properties) continue;
          const [fLng, fLat] = (f.geometry as GeoJSON.Point).coordinates;
          tryAdd(f.properties.id as string, f.properties.name as string, fLat, fLng, "heritage", f.properties.country as Country, f.properties.type as string);
        }
      } else if (layerType === "monument") {
        monumentIndex.current.forEach(m => tryAdd(m.id, m.name, m.lat, m.lng, "monument", m.country, m.monumentType));
      } else if (layerType === "barrow") {
        barrowIndex.current.forEach(b => tryAdd(b.id, b.name, b.lat, b.lng, "barrow", b.country, b.type));
      } else if (layerType === "megalith") {
        megalithIndex.current.forEach(m => tryAdd(m.id, m.name, m.lat, m.lng, "megalith", m.country, m.type));
      }
      if (candidates.length > 0) {
        candidates.sort((a, b) => a.distance - b.distance);
        data.similarSites = candidates.slice(0, 6);
      }
    };

    // One publish path for every branch that builds its enrichment as a chain.
    // Each of them used to race its own enrich() against a timeout that
    // *rejected*, and the handler translated any rejection into
    // setEnrichment(null) — so a chain that ran a second long threw away every
    // field it had already found, and the panel rendered as if the registers had
    // nothing to say. A Swedish barrow in Uppsala lost that race every time.
    //
    // What makes it worth sharing rather than fixing seven times: the failure is
    // invisible in a diff. Adding one fetch anywhere in a chain could delete
    // unrelated fields further down it, because the deadline is shared and
    // all-or-nothing. Here it resolves with the accumulator instead, and the
    // chain publishes a second time if it finishes afterwards. Branches opt in
    // by writing into the `acc` object they are handed rather than a local one.
    const runRaced = (
      acc: Partial<EnrichmentData>,
      enrich: () => Promise<Partial<EnrichmentData>>,
      pinId: string | undefined,
      ms = 8000,
    ) => {
      const deadline = { hit: false };
      const running = enrich();
      const publish = (d: Partial<EnrichmentData>): EnrichmentData | null => {
        const v = Object.keys(d).length > 0 ? d as EnrichmentData : null;
        if (v) cacheEnrichment(cacheKey, v);
        setEnrichment(v);
        return v;
      };
      Promise.race([
        running,
        new Promise<Partial<EnrichmentData>>(res => setTimeout(() => { deadline.hit = true; res(acc); }, ms)),
      ]).then(d => {
        if (cancelled) return;
        const v = publish(d);
        // Community photos are merged into the same object, so the spread is
        // what makes them visible. Skipped without a pin id: a folklore
        // selection is an array of records and has no single one to ask about.
        if (v && pinId) {
          mergeCommunityContent(pinId, v)
            .then(changed => { if (changed && !cancelled) setEnrichment({ ...v }); })
            .catch(() => {});
        }
      }).catch(() => { if (!cancelled) setEnrichment(null); })
        .finally(() => { if (!cancelled) setEnrichLoading(false); });
      // Only for the runs that went past the deadline. `acc` is the object React
      // already holds, so the late fields need a spread to show up; community
      // content merged into it meanwhile rides along rather than being lost.
      running.then(() => {
        if (cancelled || !deadline.hit) return;
        if (Object.keys(acc).length > 0) publish({ ...acc });
      }).catch(() => { /* the deadline already published what there was */ });
    };

    if (selectedType === "heritage") {
      setEnrichLoading(true);
      // Not runRaced: fetchEnrichment resolves all-or-nothing, so there is no
      // accumulator to salvage at the deadline, and this handler does a good
      // deal more after the publish than the others.
      Promise.race([
        fetchEnrichment(selected as HeritageSite, lang),
        new Promise<EnrichmentData>((_, rej) => setTimeout(() => rej(new Error("enrichment timeout")), 8000)),
      ]).then(d => {
        if (cancelled) return;
        // Cross-match: find nearby folklore records within 500m
        if (d && folkloreIndex.current.size > 0) {
          const matches: { id: string; title: string; distance: number }[] = [];
          folkloreIndex.current.forEach(f => {
            const dist = haversineKm(selected.lat, selected.lng, f.lat, f.lng);
            if (dist <= 0.5) matches.push({ id: f.id, title: f.title || f.name, distance: dist });
          });
          if (matches.length > 0) {
            matches.sort((a, b) => a.distance - b.distance);
            d.relatedFolklore = matches.slice(0, 5);
          }
        }
        // Temporal neighbors: find pins from same period
        if (d) addTemporalNeighbors(d, selected.period, selected.lat, selected.lng, selected.id);
        // Similar sites: find sites of same type in same country
        if (d) addSimilarSites(d, "heritage", selected.id, selected.lat, selected.lng, selected.country, selected.type);
        if (d) { cacheEnrichment(cacheKey, d); } setEnrichment(d);
        // Museum finds + Heritage at Risk + Community: fire in parallel, ONE batched re-render when done
        if (d) {
          Promise.allSettled([
            fetchMuseumCollections(selected.lat, selected.lng, selected.country, selected.name, lang, d.museum).catch(() => null),
            selected.country === "GB" ? checkHeritageAtRisk(selected.lat, selected.lng).catch(() => null) : Promise.resolve(null),
            mergeCommunityContent(selected.id, d).catch(() => false),
          ]).then(([museumRes, harRes, commRes]) => {
            if (cancelled) return;
            let updated = false;
            if (museumRes.status === "fulfilled" && museumRes.value && museumRes.value.length > 0) { d.museumFinds = museumRes.value; updated = true; }
            if (harRes.status === "fulfilled" && harRes.value) { d.heritageAtRisk = true; d.heritageAtRiskUrl = harRes.value.url; updated = true; }
            if (commRes.status === "fulfilled" && commRes.value === true) updated = true;
            if (updated) setEnrichment({ ...d });
          });
        }
      }).catch(() => { if (!cancelled) setEnrichment(null); }).finally(() => { if (!cancelled) setEnrichLoading(false); });
    } else if (["runestone", "battlefield", "monument", "thingsite", "route", "lighthouse"].includes(selectedType)) {
      setEnrichLoading(true);
      const acc: Partial<EnrichmentData> = {};
      const enrich = async (): Promise<Partial<EnrichmentData>> => {
        const data = acc;
        // Run multiple enrichment sources IN PARALLEL for speed
        const enrichPromises: Promise<Partial<EnrichmentData>>[] = [];
        // Source 1: Wikidata by ID (if available)
        if (selected.wikidataId) {
          enrichPromises.push(fetchWikidataImageById(selected.wikidataId, lang, selected.country).catch(() => ({})));
        }
        // Source 2: Wikipedia by URL (if available)
        const bestWikiUrl = selected.wikipediaUrls?.[lang] || selected.wikipediaUrls?.en || selected.wikipediaUrl;
        if (bestWikiUrl) {
          const match = bestWikiUrl.match(/https?:\/\/([a-z]{2,3})\.wikipedia\.org\/wiki\/(.+)/);
          if (match) {
            enrichPromises.push(fetchWikipediaDirectly(match[1], decodeURIComponent(match[2])).catch(() => ({})));
          }
        }
        // Source 3: Wikipedia by name (fast fallback for pins without wikidata/wikipedia tags)
        if (!selected.wikidataId && !bestWikiUrl && selected.name && selected.name.length > 3) {
          const searchLang = COUNTRY_WIKI_LANG[selected.country] ?? "en";
          enrichPromises.push(fetchWikipediaDirectly(searchLang, selected.name.replace(/ /g, "_")).catch(() => ({})));
        }
        // Merge all parallel results (first wins for each field)
        const enrichResults = await Promise.allSettled(enrichPromises);
        for (const r of enrichResults) {
          if (r.status !== "fulfilled") continue;
          const d = r.value;
          if (!data.image && d.image) { data.image = d.image; data.imageCredit = d.imageCredit; data.imageLicense = d.imageLicense; }
          if (!data.wikiExtract && d.wikiExtract) data.wikiExtract = d.wikiExtract;
          if (!data.wikipediaUrl && d.wikipediaUrl) data.wikipediaUrl = d.wikipediaUrl;
          if (!data.wikidataLabel && d.wikidataLabel) data.wikidataLabel = d.wikidataLabel;
          if (!data.wikiHistorySection && d.wikiHistorySection) data.wikiHistorySection = d.wikiHistorySection;
          if (!data.images && d.images) data.images = d.images;
          if (!data.heritageDesignation && d.heritageDesignation) data.heritageDesignation = d.heritageDesignation;
          if (!data.architect && d.architect) data.architect = d.architect;
          if (!data.inception && d.inception) { data.inception = d.inception; data.inceptionPrecision = d.inceptionPrecision; }
        }
        // Wikidata geo-search fallback for thingsite/route without wikidata ID
        if (!data.wikiExtract && !selected.wikidataId && (selectedType === "thingsite" || selectedType === "route")) {
          try {
            const wd = await fetchWikidataHeritage(selected.lat, selected.lng, selected.name, undefined, lang);
            if (!data.image && wd.image) data.image = wd.image;
            if (wd.wikiExtract) data.wikiExtract = wd.wikiExtract;
            if (wd.wikidataLabel) data.wikidataLabel = wd.wikidataLabel;
            if (wd.wikipediaUrl) data.wikipediaUrl = wd.wikipediaUrl;
          } catch { /* silent */ }
        }
        // Rundata enrichment: fetch runic inscription data from rundata.info API
        if (selectedType === "runestone" && selected.wikidataId) {
          try {
            const rundataMapping = await fetch("/data/rundata-mapping.json").then(r => r.json()).catch(() => ({}));
            const corpusId = rundataMapping[selected.wikidataId];
            if (corpusId) {
              const slug = corpusId.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
              const rd = await fetch(`https://rundata.info/api/inscription/${slug}`, { signal: AbortSignal.timeout(6000) }).then(r => r.json()).catch(() => null);
              if (rd?.meta) {
                interface RuneText { language_code: string; value?: string }
                const texts: RuneText[] = rd.meta.runic_texts ?? [];
                const transliteration = texts.find((t) => t.language_code === "run")?.value;
                const translation = texts.find((t) => t.language_code === "eng")?.value
                  || texts.find((t) => t.language_code === "swe")?.value;
                const normalized = texts.find((t) => t.language_code === "fvn")?.value;
                if (transliteration && !data.transliteration) data.transliteration = transliteration;
                if (translation && !data.runicTranslation) data.runicTranslation = translation;
                if (normalized && !data.runicNormalized) data.runicNormalized = normalized;
                if (rd.signature && !data.corpusId) data.corpusId = rd.signature;
                if (rd.meta.dating) data.runicDating = rd.meta.dating;
                if (rd.meta.material) data.runicMaterial = rd.meta.material;
              }
            }
          } catch { /* silent */ }
        }
        // Runestone fallback: geo-search Wikipedia by coordinates (strict title match)
        if (!data.wikiExtract && selectedType === "runestone") {
          const searched = await searchWikipediaRunestone(selected.lat, selected.lng, selected.name, selected.country, lang);
          if (!data.image && searched.image) data.image = searched.image;
          if (!data.wikiExtract && searched.wikiExtract) data.wikiExtract = searched.wikiExtract;
          if (!data.wikidataLabel && searched.wikidataLabel) data.wikidataLabel = searched.wikidataLabel;
        }
        // Lokalhistoriewiki for Norwegian pins — only as fallback, never overwrite archaeological descriptions
        if (selected.country === "NO") {
          try {
            const lhw = await fetchLokalhistoriewiki(selected.lat, selected.lng, selected.name);
            if (lhw.lokalhistoriewikiUrl) data.lokalhistoriewikiUrl = lhw.lokalhistoriewikiUrl;
            if (!data.wikiExtract && lhw.wikiExtract) data.wikiExtract = lhw.wikiExtract;
            if (!data.image && lhw.image) { data.image = lhw.image; data.imageCredit = lhw.imageCredit; data.imageLicense = lhw.imageLicense; }
          } catch { /* silent */ }
        }
        // Lex.dk fallback for Danish pins
        if (selected.country === "DK" && (!data.wikiExtract || !data.image)) {
          try {
            const lex = await fetchLexDk(selected.name);
            if (lex.lexUrl) data.lexUrl = lex.lexUrl;
            if (!data.wikiExtract && lex.wikiExtract) data.wikiExtract = lex.wikiExtract;
            if (!data.image && lex.image) { data.image = lex.image; data.imageCredit = lex.imageCredit; data.imageLicense = lex.imageLicense; }
          } catch { /* silent */ }
        }
        // Curated descriptions from JSON files
        if (selectedType === "battlefield") {
          const ev = selected as HistoricalEvent;
          const evCat = ev.eventCategory || "battlefield";
          const isMilitary = evCat === "battlefield" || evCat === "siege" || evCat === "naval_battle";
          if (isMilitary) {
            try {
              const curated = await loadBattlefieldCurated();
              const osmId = selected.id.replace(/^osm-battle-/, "");
              const wdId = selected.id.replace(/^wd-battle-/, "").replace(/^wd-event-/, "");
              const entry = curated[osmId] || curated[wdId] || (selected.wikidataId ? curated[selected.wikidataId] : undefined);
              if (entry) data.curatedBattlefield = entry;
            } catch { /* silent */ }
          }
        }
        if (selectedType === "runestone") {
          try {
            const curated = await loadRunestoneCurated();
            const osmId = selected.id.replace(/^osm-rune-/, "");
            const entry = curated[osmId];
            if (entry) data.curatedRunestone = entry;
          } catch { /* silent */ }
        }
        // Wikipedia article titled with this item's own name. Never the type:
        // see the header on fetchWikipediaTitleImage for what that borrows.
        // The coordinates are the second half of that check, and they are why a
        // Manx church called "Saint Paul's" no longer shows the London cathedral.
        // Skip geo-search fallbacks for battlefields — modern photos at battle sites are irrelevant
        if (!data.image && selected.country && selected.name && selectedType !== "battlefield") {
          try {
            const fallback = await fetchWikipediaTitleImage(selected.name, selected.country, selected.lat, selected.lng);
            if (fallback.image && isRelevantImageUrl(fallback.image)) data.image = fallback.image;
          } catch { /* silent */ }
        }
        // Commons geo-search fallback — only for site types where nearby photos are likely relevant
        // Skip for heritage/rockart/industrial/battlefield/shipwreck: nearby photos are usually unrelated modern buildings
        const commonsEligible = ["monument", "building", "museum", "unesco", "thingsite"].includes(selectedType);
        const hasRealName = selected.name && !/^(Ukendt|Unknown|Ukjent)\b/i.test(selected.name);
        if (!data.image && commonsEligible && hasRealName) {
          try {
            const commons = await fetchCommonsNearby(selected.lat, selected.lng, 300, selected.name);
            if (commons.length > 0) {
              data.image = commons[0].url;
              data.imageCredit = commons[0].credit;
              data.imageLicense = commons[0].license;
              data.images = [...(data.images ?? []), ...commons];
            }
          } catch { /* silent */ }
        }
        // Geograph fallback for GB/IE/IM — millions of geo-located CC photos of the British Isles
        if (!data.image && (selected.country === "GB" || selected.country === "IE" || selected.country === "IM") && hasRealName) {
          try {
            const geoPhotos = await fetchGeographPhotos(selected.lat, selected.lng, selected.name);
            if (geoPhotos.length > 0) {
              data.image = geoPhotos[0].url;
              data.imageCredit = geoPhotos[0].credit;
              data.imageLicense = geoPhotos[0].license;
              data.images = [...(data.images ?? []), ...geoPhotos];
            }
          } catch { /* silent */ }
        }
        // Europeana items for monument/runestone/battlefield
        try {
          const europeana = await fetchEuropeana(selected.name, selected.country, undefined, selected.lat, selected.lng, undefined, selectedType);
          if (europeana.length > 0) { data.europeanaItems = europeana; data.europeanaTotal = (europeana as EuropeanaResults)._total; data.europeanaSearchUrl = (europeana as EuropeanaResults)._searchUrl; }
        } catch { /* silent */ }
        // Museum finds: notable items from this location that are in museum collections
        try {
          const finds = await fetchMuseumCollections(selected.lat, selected.lng, selected.country, selected.name, lang);
          if (finds && finds.length > 0) data.museumFinds = finds;
        } catch { /* silent */ }
        // Knowledge graph: Wikidata relations
        if (selected.wikidataId) {
          try {
            const rels = await fetchWikidataRelations(selected.wikidataId, lang);
            if (rels.length > 0) data.wikidataRelations = rels;
          } catch { /* silent */ }
        }
        // Cross-match: find nearby folklore records within 500m (monument/thingsite)
        if (["monument", "thingsite"].includes(selectedType) && folkloreIndex.current.size > 0) {
          const matches: { id: string; title: string; distance: number }[] = [];
          folkloreIndex.current.forEach(f => {
            const dist = haversineKm(selected.lat, selected.lng, f.lat, f.lng);
            if (dist <= 0.5) matches.push({ id: f.id, title: f.title || f.name, distance: dist });
          });
          if (matches.length > 0) {
            matches.sort((a, b) => a.distance - b.distance);
            data.relatedFolklore = matches.slice(0, 5);
          }
        }
        // Temporal neighbors: find pins from same period
        const temporalPeriod = selectedType === "battlefield" ? selected.date
          : selectedType === "monument" ? selected.startDate
          : selectedType === "runestone" ? "Vikingetid"
          : undefined;
        addTemporalNeighbors(data, temporalPeriod, selected.lat, selected.lng, selected.id);
        // Similar sites: same type in same country
        const simType = selectedType === "monument" ? selected.monumentType
          : selectedType === "barrow" ? selected.type
          : selectedType === "megalith" ? selected.type
          : undefined;
        if (simType) addSimilarSites(data, selectedType, selected.id, selected.lat, selected.lng, selected.country, simType);
        return data;
      };
      runRaced(acc, enrich, selected.id);
    } else if (selectedType === "building") {
      setEnrichLoading(true);
      const acc: Partial<EnrichmentData> = {};
      const enrich = async (): Promise<Partial<EnrichmentData>> => {
        const data = acc;
        const bld = selected as ProtectedBuilding;
        // Started here and merged when it lands. It used to run after the
        // publish and mutate the already-rendered object without telling React,
        // so the badge only appeared if something else happened to re-render the
        // panel. One request, and it no longer holds anything else up.
        const atRisk: Promise<void> | null = bld.country === "GB"
          ? checkHeritageAtRisk(bld.lat, bld.lng)
              .then(har => { if (har) { data.heritageAtRisk = true; data.heritageAtRiskUrl = har.url; } })
              .catch(() => { /* silent */ })
          : null;
        // Dutch buildings, started here for the same reason. The RCE listing text
        // and the P359 join to Wikidata ran at a median of 2.2 s and a worst case
        // of 3.6 s over six monuments measured 19 Aug 2026, with WDQS as the slow
        // half. Awaited in sequence that is more than a quarter of the 8 s
        // deadline spent before Europeana or Commons has started.
        const dutch: Promise<void> | null = bld.country === "NL" && bld.id.startsWith("nl-building-")
          ? fetchDutchDetail(bld.id.slice("nl-building-".length))
              .then(d => {
                if (d.description) data.description = d.description;
                if (d.image && !data.image) data.image = d.image;
                if (d.wikipediaUrl && !data.wikipediaUrl) data.wikipediaUrl = d.wikipediaUrl;
              })
              .catch(() => { /* silent */ })
          : null;
        // Lower Saxon buildings: the listing text the viewport page leaves
        // out, one request per record against the Denkmalatlas API.
        const lowerSaxon: Promise<void> | null = bld.country === "DE" && bld.id.startsWith("de-ni-bld-")
          ? fetchNiedersachsenDetail(bld.id.slice("de-ni-bld-".length))
              .then(d => { if (d.description && !data.description) data.description = d.description; })
              .catch(() => { /* silent */ })
          : null;
        // Danish buildings: BBR facts and photographs from the bygning page the
        // pin links to, and the fredningsbeskrivelse from the sag one hop
        // behind it. See fetchDanishBuildingDetail for why the second hop is
        // where all the prose is.
        if (bld.country === "DK" && bld.sourceUrl.includes("kulturarv.dk/fbb")) {
          try {
            Object.assign(data, await fetchDanishBuildingDetail(bld.sourceUrl));
          } catch { /* silent */ }
        }
        // Norwegian buildings: fetch KMS API for all images
        if (bld.country === "NO" && bld.kulturminnesokId) {
          try {
            const numericId = bld.kulturminnesokId.replace(/-.*$/, "");
            const kmsRes = await fetch(`https://kms-api.kulturminnesok.no/api/v2/search/${numericId}`, { signal: AbortSignal.timeout(8000) });
            if (kmsRes.ok) {
              const kmsData = await kmsRes.json();
              const kmsImages: { url: string; credit?: string; license?: string }[] = [];
              for (const img of (kmsData.images ?? []).slice(0, 5)) {
                const url = sanitizeImageUrl(img.thumbnail || img.url);
                if (url) {
                  kmsImages.push({
                    url,
                    credit: img.photograph || img.photographer || undefined,
                    license: img.license?.includes("CC") ? img.license.split(" - ")[0].trim() : img.license,
                  });
                }
              }
              if (kmsImages.length > 0) {
                data.image = kmsImages[0].url;
                data.imageCredit = kmsImages[0].credit;
                data.imageLicense = kmsImages[0].license;
                data.images = kmsImages;
              }
            }
          } catch { /* silent */ }
        }
        if (bld.country === "NO" && bld.description) {
          data.description = bld.description;
        }
        // Swedish buildings: fetch JSON-LD from kulturarvsdata.se
        if (bld.country === "SE" && bld.sourceUrl.includes("kulturarvsdata.se")) {
          try {
            const res = await fetch(bld.sourceUrl, { headers: { Accept: "application/json" }, signal: AbortSignal.timeout(8000) });
            if (res.ok) {
              const json = await res.json();
              interface KsamValue { "@value"?: string }
              interface KsamItem {
                "@type"?: string;
                "ksam:desc"?: KsamValue;
                "ksam:lowresSource"?: KsamValue;
                "ksam:thumbnailSource"?: KsamValue;
                "ksam:byline"?: KsamValue;
              }
              const graph: KsamItem[] = json["@graph"] ?? [];
              const parts: string[] = [];
              for (const item of graph) {
                if (item["@type"] !== "ksam:ItemDescription") continue;
                const desc = item["ksam:desc"]?.["@value"] ?? "";
                if (desc) parts.push(desc);
              }
              if (parts.length) data.description = parts.join("\n\n");
              // Extract K-samsök images
              const seImages: { url: string; credit?: string; license?: string }[] = [];
              for (const item of graph) {
                const lowres = item["ksam:lowresSource"]?.["@value"] ?? item["ksam:thumbnailSource"]?.["@value"];
                if (lowres) {
                  const url = sanitizeImageUrl(lowres);
                  if (url) seImages.push({ url, credit: item["ksam:byline"]?.["@value"] || undefined });
                }
              }
              if (seImages.length > 0) {
                data.image = seImages[0].url;
                data.imageCredit = seImages[0].credit;
                data.images = seImages;
              }
            }
          } catch { /* silent */ }
        }
        // Wikidata image + Wikipedia text fallback
        try {
          const wd = await fetchWikidataHeritage(bld.lat, bld.lng, bld.name, undefined, lang);
          if (!data.image && wd.image) data.image = wd.image;
          if (!data.description && !data.wikiExtract && wd.wikiExtract) data.wikiExtract = wd.wikiExtract;
          if (wd.wikidataLabel) data.wikidataLabel = wd.wikidataLabel;
        } catch { /* silent */ }
        // Direct Wikipedia lookup for buildings without description (IS, GL, FI, etc.)
        // The guard used to be a string comparison against "Fredet bygning",
        // the one placeholder that existed when it was written. It is really a
        // question about the data: did the register name this building, or is
        // the name just its type word? Where the two are equal the mapper fell
        // back, and handing a common noun to Wikipedia returns the article
        // about the concept — the Norwegian layer would have attached "bygård"
        // to 119 unnamed Oslo enkeltminner and "veganlegg" to a road.
        if (!data.description && !data.wikiExtract && bld.name && bld.name !== bld.type) {
          const wikiLangs = [COUNTRY_WIKI_LANG[bld.country], lang, "en"].filter(Boolean) as string[];
          for (const wl of [...new Set(wikiLangs)]) {
            try {
              const direct = await fetchWikipediaDirectly(wl, bld.name);
              if (direct.wikiExtract) { data.wikiExtract = direct.wikiExtract; if (!data.image && direct.image) data.image = direct.image; if (direct.wikipediaUrl) data.wikipediaUrl = direct.wikipediaUrl; break; }
            } catch { /* silent */ }
          }
        }
        // Wikipedia from linked URL (if available)
        if (!data.wikiExtract && bld.wikipediaUrl) {
          const match = bld.wikipediaUrl.match(/https?:\/\/([a-z]{2,3})\.wikipedia\.org\/wiki\/(.+)/);
          if (match) {
            try {
              const direct = await fetchWikipediaDirectly(match[1], decodeURIComponent(match[2]));
              if (!data.image && direct.image) data.image = direct.image;
              if (!data.wikiExtract && direct.wikiExtract) data.wikiExtract = direct.wikiExtract;
              if (!data.wikipediaUrl && direct.wikipediaUrl) data.wikipediaUrl = direct.wikipediaUrl;
            } catch { /* silent */ }
          }
        }
        // Commons geo-search fallback
        if (!data.image) {
          try {
            const commons = await fetchCommonsNearby(bld.lat, bld.lng, 300, bld.name);
            if (commons.length > 0) {
              data.image = commons[0].url;
              data.imageCredit = commons[0].credit;
              data.imageLicense = commons[0].license;
              data.images = [...(data.images ?? []), ...commons];
            }
          } catch { /* silent */ }
        }
        // Europeana items for buildings
        try {
          const europeana = await fetchEuropeana(bld.name, bld.country, undefined, bld.lat, bld.lng, undefined, "building");
          if (europeana.length > 0) { data.europeanaItems = europeana; data.europeanaTotal = (europeana as EuropeanaResults)._total; data.europeanaSearchUrl = (europeana as EuropeanaResults)._searchUrl; }
        } catch { /* silent */ }
        // Already merged by its own handler; awaited only so a chain that beats
        // the deadline returns a complete result.
        if (atRisk) await atRisk;
        if (dutch) await dutch;
        if (lowerSaxon) await lowerSaxon;
        return data;
      };
      runRaced(acc, enrich, selected.id);
    } else if (selectedType === "folklore") {
      // Folklore: try Wikipedia geo-search + Commons for a contextual background image
      const records = selected as FolkloreRecord[];
      const rec = Array.isArray(records) ? records[0] : records;
      if (rec?.lat && rec?.lng) {
        setEnrichLoading(true);
        const acc: Partial<EnrichmentData> = {};
        const enrich = async (): Promise<Partial<EnrichmentData>> => {
          const data = acc;
          // Irish mythology: use wikidataId or wikipediaUrl for rich enrichment
          if (rec.source === "irish-mythology" && (rec.wikidataId || rec.wikipediaUrl)) {
            try {
              if (rec.wikidataId) {
                const wd = await fetchWikidataImageById(rec.wikidataId, lang);
                if (wd.image) { data.image = wd.image; data.imageCredit = wd.imageCredit; data.imageLicense = wd.imageLicense; }
                if (wd.images) data.images = wd.images;
                if (wd.wikiExtract) data.wikiExtract = wd.wikiExtract;
                if (wd.wikiHistorySection) data.wikiHistorySection = wd.wikiHistorySection;
                if (wd.wikipediaUrl) data.wikipediaUrl = wd.wikipediaUrl;
              } else if (rec.wikipediaUrl) {
                const match = rec.wikipediaUrl.match(/https?:\/\/([a-z]{2,3})\.wikipedia\.org\/wiki\/(.+)/);
                if (match) {
                  const wiki = await fetchWikipediaDirectly(match[1], decodeURIComponent(match[2]));
                  if (wiki.image) { data.image = wiki.image; data.imageCredit = wiki.imageCredit; }
                  if (wiki.images) data.images = wiki.images;
                  data.wikipediaUrl = rec.wikipediaUrl;
                }
              }
            } catch { /* silent */ }
            // Europeana items for Irish mythology sites
            try {
              const europeana = await fetchEuropeana(rec.name, rec.country, undefined, rec.lat, rec.lng, undefined, "folklore");
              if (europeana.length > 0) {
                data.europeanaItems = europeana;
                if (!data.image) {
                  const euroImg = europeana.find(e => e.fullImage);
                  if (euroImg?.fullImage) { data.image = euroImg.fullImage; data.imageCredit = euroImg.dataProvider ?? "Europeana"; }
                }
              }
            } catch { /* silent */ }
          } else {
          // Wikipedia geo-search (non-Irish)
          // Local language first, then the Nordic neighbours that often carry
          // the same legend, then English. The Baltic pair land on et/lv here
          // instead of the old "sv, da, en" catch-all.
          const langOrder = rec.country === "GB" || rec.country === "IE" || rec.country === "IM"
            ? ["en"]
            : [...new Set([COUNTRY_WIKI_LANG[rec.country] ?? "sv", "sv", "da", "en"])];
          for (const wLang of langOrder) {
            if (data.image) break;
            try {
              const res = await fetch(`https://${wLang}.wikipedia.org/w/api.php?action=query&list=geosearch&gscoord=${rec.lat}|${rec.lng}&gsradius=1000&gslimit=3&format=json&origin=*`);
              if (!res.ok) continue;
              const results = (await res.json()).query?.geosearch ?? [];
              for (const hit of results) {
                const wiki = await fetchWikipediaDirectly(wLang, hit.title);
                if (wiki.image) { data.image = wiki.image; data.imageCredit = wiki.imageCredit; break; }
              }
            } catch { /* silent */ }
          }
          } // end else
          // Atmospheric art for folklore
          try {
            const artData = await loadHeritageArt();
            const art = getAtmosphericArt(artData, "folklore", rec.country, rec.category);
            if (art) data.atmosphericArt = art;
          } catch { /* silent */ }
          // Cross-match: find nearby heritage sites within 500m
          if (heritageFeatures.length > 0) {
            const matches: { id: string; name: string; type: string; distance: number }[] = [];
            for (const f of heritageFeatures) {
              if (f.geometry.type !== "Point" || !f.properties?.id) continue;
              const [fLng, fLat] = (f.geometry as GeoJSON.Point).coordinates;
              const dist = haversineKm(rec.lat, rec.lng, fLat, fLng);
              if (dist <= 0.5) matches.push({ id: f.properties.id, name: f.properties.name || f.properties.type, type: f.properties.type || "", distance: dist });
            }
            if (matches.length > 0) {
              matches.sort((a, b) => a.distance - b.distance);
              data.relatedHeritage = matches.slice(0, 5);
            }
          }
          return data;
        };
        // No pin id on purpose. `selected` is the array of records for this
        // pin and `selected.id` was undefined here, so the community merge
        // queried pin_id=undefined every time. The panel asks for community
        // content itself, per record, and pages between them.
        runRaced(acc, enrich, undefined);
      }
    } else if (selectedType === "shipwreck" || selectedType === "rockart" || selectedType === "industrial" || selectedType === "barrow" || selectedType === "megalith" || selectedType === "townscape") {
      setEnrichLoading(true);
      // The longest chain in the app — the register record, Europeana, a museum
      // bbox search and two Wikidata SPARQL queries, serially — and the reason
      // runRaced exists. A Swedish barrow in Uppsala lost the old rejecting race
      // every time, so its panel rendered with no enrichment at all.
      const acc: Partial<EnrichmentData> = {};
      const enrich = async (): Promise<Partial<EnrichmentData>> => {
        const data = acc;
        // Swedish barrows, rock art, industrial sites and wrecks: the same
        // Kulturmiljöregistret record the heritage panel reads. These four are
        // the layers that never set `raaUuid` — they keep the lämning UUID
        // inside the Fornsök link they show the reader — so the id is parsed
        // back out of it rather than threaded through four site types.
        //
        // Started first and merged the moment it lands, not where it is read.
        // enrich() is raced against an 8 s deadline, and awaited in place this
        // pushed Europeana, the museum bbox search and two Wikidata SPARQL
        // queries past it — the panel then rendered with *no* enrichment at all,
        // which is worse than the fields it was meant to add. Merging on arrival
        // rather than at the end of the chain also means the register columns are
        // in the accumulator by the time the deadline publishes, so a slow
        // Wikidata query costs the reader a "did you know", not the record.
        //
        // Worth more here than on heritage. None of these four site types has a
        // field for the antikvariska bedömning, so the category
        // searchAllBedomningar already knows was being dropped on the floor: a
        // Swedish barrow panel showed no protection at all. This restores that,
        // adds the parish and province, and brings the eleven per-site columns.
        const raaUuid = selected.country === "SE"
          ? /app\.raa\.se\/open\/fornsok\/lamning\/([0-9a-f-]{36})/.exec(selected.sourceUrl ?? "")?.[1]
          : undefined;
        const swedishDetail: Promise<void> | null = raaUuid
          ? fetchSwedishDetail(raaUuid).then(detail => {
              // Only where the register is the better source. The description
              // also comes down with the bbox search; the record's differs in
              // keeping its paragraph breaks, so it wins on length alone.
              if (detail.description && detail.description.length > (data.wikiExtract?.length ?? 0)) data.wikiExtract = detail.description;
              if (detail.municipality) data.municipality = detail.municipality;
              if (detail.protectionStatus) data.protectionStatus = detail.protectionStatus;
              if (detail.terrain) data.terrain = detail.terrain;
              if (detail.orientation) data.orientation = detail.orientation;
              if (detail.vegetation) data.vegetation = detail.vegetation;
              if (detail.components) data.components = detail.components;
              if (detail.tradition) data.tradition = detail.tradition;
              if (detail.references?.length) data.references = detail.references;
              if (detail.investigationStatus) data.investigationStatus = detail.investigationStatus;
              if (detail.damageStatus) data.damageStatus = detail.damageStatus;
              if (detail.registerNumber) data.registerNumber = detail.registerNumber;
              if (detail.notVisible) data.notVisible = true;
              if (detail.fieldChecked === false) data.fieldChecked = false;
              if (detail.images) data.images = [...(data.images ?? []), ...detail.images];
              if (detail.image && !data.image) { data.image = detail.image; data.imageCredit = detail.imageCredit; data.imageLicense = detail.imageLicense; }
            }).catch(() => { /* silent */ })
          : null;
        // Shipwreck: fetch structured ship data from Wikidata (type, navy, builder, dates, conflict)
        // For wrecks without wikidataId (from national APIs), search Wikidata by name
        let wreckQid = selected.wikidataId;
        if (selectedType === "shipwreck" && !wreckQid && selected.name && selected.name.length > 4) {
          try {
            const searchRes = await fetch(
              `https://www.wikidata.org/w/api.php?action=wbsearchentities&search=${encodeURIComponent(selected.name)}&language=en&type=item&limit=5&format=json&origin=*`,
              { signal: AbortSignal.timeout(5000) }
            );
            if (searchRes.ok) {
              const searchData = await searchRes.json();
              interface WdSearchHit { id?: string; label?: string; description?: string }
              // Find exact name match (skip disambiguation pages)
              const match = ((searchData.search ?? []) as WdSearchHit[]).find((r) =>
                r.label?.toLowerCase() === selected.name.toLowerCase() &&
                !r.description?.includes("disambiguation")
              );
              if (match?.id) wreckQid = match.id;
            }
          } catch { /* silent */ }
        }
        if (selectedType === "shipwreck" && wreckQid) {
          try {
            const ship = await fetchWikidataShipDetail(wreckQid, lang);
            if (ship.image && !data.image) data.image = ship.image;
            if (ship.shipType) data.shipType = ship.shipType;
            if (ship.operator) data.shipOperator = ship.operator;
            if (ship.manufacturer) data.shipManufacturer = ship.manufacturer;
            if (ship.built) data.shipBuilt = ship.built;
            if (ship.sunk) data.shipSunk = ship.sunk;
            if (ship.conflict) data.shipConflict = ship.conflict;
            if (ship.length) data.shipLength = ship.length;
            if (ship.description && !data.description) data.description = ship.description;
            if (ship.wikipediaUrl && !data.wikipediaUrl) data.wikipediaUrl = ship.wikipediaUrl;
          } catch { /* silent */ }
        }
        // Townscape: use wikidataId for city article + Commons category for building gallery
        if (selectedType === "townscape" && selected.wikidataId) {
          try {
            const wd = await fetchWikidataImageById(selected.wikidataId, lang, selected.country);
            if (wd.image) data.image = wd.image;
            if (wd.wikiExtract) data.wikiExtract = wd.wikiExtract;
            // The URL travels with the text: without it ForeignWikiNotice cannot say
            // which Wikipedia the text is from, and the panel has no link to it.
            if (wd.wikiExtract && wd.wikipediaUrl) data.wikipediaUrl = wd.wikipediaUrl;
            if (wd.wikidataLabel) data.wikidataLabel = wd.wikidataLabel;
            if (wd.imageCredit) data.imageCredit = wd.imageCredit;
            if (wd.imageLicense) data.imageLicense = wd.imageLicense;
            if (wd.images) data.images = wd.images;
            if (wd.wikiHistorySection) data.wikiHistorySection = wd.wikiHistorySection;
          } catch { /* silent */ }
          // Fetch extra Commons images from "Buildings in X" category for architecture gallery
          const cat = selected.commonsCategory;
          if (cat) {
            try {
              const buildingImages = await fetchCommonsCategoryImages(`Buildings in ${cat}`, 6, selected.name);
              if (buildingImages.length > 0) {
                const existing = data.images ?? [];
                const existingUrls = new Set([data.image, ...existing.map(i => i.url)].filter(Boolean));
                const fresh = buildingImages.filter(ci => !existingUrls.has(ci.url));
                data.images = [...existing, ...fresh];
              }
            } catch { /* silent */ }
          }
        } else
        // DK Kulturmiljø without wikidataId: use stored wikiUrl for image enrichment
        if (selectedType === "townscape" && selected.wikiUrl && !selected.wikidataId) {
          try {
            const wikiUrl = selected.wikiUrl as string;
            const langMatch = wikiUrl.match(/https?:\/\/([a-z]{2,3})\.wikipedia/);
            const titleMatch = wikiUrl.match(/\/wiki\/(.+)$/);
            if (langMatch && titleMatch) {
              const wiki = await fetchWikipediaDirectly(langMatch[1], decodeURIComponent(titleMatch[1]));
              if (wiki.image) { data.image = wiki.image; data.imageCredit = wiki.imageCredit; data.imageLicense = wiki.imageLicense; }
              if (wiki.images) data.images = wiki.images;
              if (wiki.wikiHistorySection) data.wikiHistorySection = wiki.wikiHistorySection;
              data.wikipediaUrl = wikiUrl;
            }
          } catch { /* silent */ }
        } else
        // Megalith: use wikidataId directly for richer enrichment
        if ((selectedType === "megalith") && selected.wikidataId) {
          try {
            const wd = await fetchWikidataImageById(selected.wikidataId, lang, selected.country);
            if (wd.image) data.image = wd.image;
            if (wd.wikiExtract) data.wikiExtract = wd.wikiExtract;
            if (wd.wikidataLabel) data.wikidataLabel = wd.wikidataLabel;
            if (wd.wikipediaUrl) data.wikipediaUrl = wd.wikipediaUrl;
            if (wd.imageCredit) data.imageCredit = wd.imageCredit;
            if (wd.imageLicense) data.imageLicense = wd.imageLicense;
            if (wd.images) data.images = wd.images;
            if (wd.heritageDesignation) data.heritageDesignation = wd.heritageDesignation;
            if (wd.architect) data.architect = wd.architect;
            if (wd.inception) { data.inception = wd.inception; data.inceptionPrecision = wd.inceptionPrecision; }
          } catch { /* silent */ }
          // Also try Wikipedia directly for full article extract
          if (selected.wikipediaUrl) {
            try {
              const wikiUrl = selected.wikipediaUrl as string;
              const langMatch = wikiUrl.match(/https?:\/\/([a-z]{2,3})\.wikipedia/);
              const titleMatch = wikiUrl.match(/\/wiki\/(.+)$/);
              if (langMatch && titleMatch) {
                const wiki = await fetchWikipediaDirectly(langMatch[1], decodeURIComponent(titleMatch[1]));
                if (wiki.wikiExtract && (!data.wikiExtract || wiki.wikiExtract.length > data.wikiExtract.length)) data.wikiExtract = wiki.wikiExtract;
                if (wiki.image && !data.image) { data.image = wiki.image; data.imageCredit = wiki.imageCredit; }
                if (wiki.images) data.images = [...(data.images ?? []), ...wiki.images];
                if (wiki.wikiHistorySection) data.wikiHistorySection = wiki.wikiHistorySection;
              }
            } catch { /* silent */ }
          }
          // Relevance check: discard Wikipedia extract if it's about a place/manor
          // rather than the megalith itself (e.g. "Selchausdal" article about the manor)
          if (data.wikiExtract) {
            const wt = data.wikiExtract.toLowerCase();
            const MEGALITH_TERMS = /jættestue|dysse|stendysse|stordysse|langdysse|megalit|storstensgrav|gravkammer|stenkammer|passage.?grave|dolmen|dyssekammer|stenalder|bronzealder|neoli|gravplads|oldtid|forhistorisk|fortidsminde|gravhøj|stenkiste|hällkista|gånggrift|megalith|burial.?chamber|stone.?age|prehistoric|hünen|gangbau|ganggrav|jettegryte|steinsetning/;
            if (!MEGALITH_TERMS.test(wt)) {
              data.wikiExtract = undefined;
              data.wikiHistorySection = undefined;
            }
          }
        } else if (selectedType !== "townscape" && selectedType !== "barrow" || (selectedType === "townscape" && !selected.description)) {
          // Wikidata image by coordinates — skip for townscape (own descriptions) and barrows (name collisions with palaces/manors)
          try {
            const wd = await fetchWikidataHeritage(selected.lat, selected.lng, selected.name, undefined, lang);
            if (wd.image && isRelevantImageUrl(wd.image)) data.image = wd.image;
            if (wd.wikiExtract) data.wikiExtract = wd.wikiExtract;
            // The URL travels with the text: without it ForeignWikiNotice cannot say
            // which Wikipedia the text is from, and the panel has no link to it.
            if (wd.wikiExtract && wd.wikipediaUrl) data.wikipediaUrl = wd.wikipediaUrl;
            if (wd.wikidataLabel) data.wikidataLabel = wd.wikidataLabel;
          } catch { /* silent */ }
        } else if (selectedType === "townscape" && !data.image) {
          // Townscape with own description but no image yet — try coordinate-based image only
          try {
            const wd = await fetchWikidataHeritage(selected.lat, selected.lng, selected.name, undefined, lang);
            if (wd.image) data.image = wd.image;
            // Don't override own description with coordinate-matched wiki text
          } catch { /* silent */ }
        }
        // Wikipedia name search for pins without wikidata (works for GB castles, wrecks, etc.)
        // Try primary language first, then de/fr fallback (many Irish megaliths only have de articles)
        if (!data.wikiExtract && !selected.wikidataId && selected.name && selected.name.length > 3 && !/^(Vrag|Wreck|Unknown|Ukendt)/i.test(selected.name)) {
          const primaryLang = COUNTRY_WIKI_LANG[selected.country] ?? "en";
          const searchLangs = selected.country === "GB" || selected.country === "IE" || selected.country === "IM"
            ? [primaryLang, "de", "fr"] : [primaryLang];
          for (const sLang of searchLangs) {
            if (data.wikiExtract) break;
            try {
              const direct = await fetchWikipediaDirectly(sLang, selected.name.replace(/ /g, "_"));
              if (direct.wikiExtract) data.wikiExtract = direct.wikiExtract;
              if (!data.image && direct.image) { data.image = direct.image; data.imageCredit = direct.imageCredit; }
              if (!data.wikidataLabel && direct.wikidataLabel) data.wikidataLabel = direct.wikidataLabel;
              if (!data.wikipediaUrl && direct.wikipediaUrl) data.wikipediaUrl = direct.wikipediaUrl;
              if (direct.wikiHistorySection) data.wikiHistorySection = direct.wikiHistorySection;
            } catch { /* silent */ }
          }
        }
        // Finnish Kyppi.fi detail page — descriptions for shipwrecks, barrows, industrial, rock art
        if (selected.country === "FI" && selected.sourceUrl?.includes("kyppi.fi")) {
          try {
            const fi = await fetchFinnishDetail(selected.sourceUrl);
            if (fi.description && (!data.wikiExtract || fi.description.length > (data.wikiExtract?.length ?? 0))) data.wikiExtract = fi.description;
          } catch { /* silent */ }
        }
        // SHFA images for Swedish rock art — rich photographic archive
        if (selectedType === "rockart" && selected.country === "SE" && selected.id.startsWith("se-rock-")) {
          try {
            const shfaImgs = await import("../api/rockart.ts").then(m => m.fetchSHFAImages(selected.id));
            if (shfaImgs.length > 0) {
              if (!data.image) { data.image = shfaImgs[0].url; data.imageCredit = shfaImgs[0].credit; data.imageLicense = shfaImgs[0].license; }
              data.images = [...(data.images ?? []), ...shfaImgs];
            }
          } catch { /* silent */ }
        }
        // Danish rock art / barrows / megaliths / shipwrecks: scrape kulturarv.dk detail page for description and images
        {
          const kulturarvUrl = (selectedType === "megalith") ? selected.kulturarvUrl : ((selectedType === "rockart" || selectedType === "barrow" || selectedType === "shipwreck") && selected.country === "DK") ? selected.sourceUrl : null;
          if (kulturarvUrl) {
            try {
              const detail = await fetchDanishDetail(kulturarvUrl);
              if (detail.placeName && !data.wikidataLabel) data.wikidataLabel = detail.placeName;
              if (detail.description && (!data.wikiExtract || detail.description.length > (data.wikiExtract?.length ?? 0))) data.wikiExtract = detail.description;
              if (detail.municipality) data.municipality = detail.municipality;
              if (detail.protectionStatus) { data.heritageDesignation = detail.protectionStatus; data.protectionStatus = detail.protectionStatus; }
              if (detail.museum) data.museum = detail.museum;
              if (detail.image && !data.image) { data.image = detail.image; data.imageCredit = detail.imageCredit; data.imageLicense = detail.imageLicense; }
              if (detail.images) data.images = [...(data.images ?? []), ...detail.images];
            } catch { /* silent */ }
          }
        }
        // Lokalhistoriewiki for Norwegian pins — only as fallback, never overwrite archaeological descriptions
        // Skip townscapes (large areas get irrelevant nearby articles)
        if (selected.country === "NO" && selectedType !== "townscape") {
          try {
            const lhw = await fetchLokalhistoriewiki(selected.lat, selected.lng, selected.name);
            if (lhw.lokalhistoriewikiUrl) data.lokalhistoriewikiUrl = lhw.lokalhistoriewikiUrl;
            if (!data.wikiExtract && lhw.wikiExtract) data.wikiExtract = lhw.wikiExtract;
            if (!data.image && lhw.image) { data.image = lhw.image; data.imageCredit = lhw.imageCredit; data.imageLicense = lhw.imageLicense; }
          } catch { /* silent */ }
        }
        // Lex.dk for Danish pins — prefer enriched Wikidata label over raw name
        // Skip barrows: name collisions with palaces/manors (e.g. "Ledreborg" → Ledreborg Slot article)
        if (selected.country === "DK" && selectedType !== "barrow" && (!data.wikiExtract || !data.image)) {
          const lexName = data.wikidataLabel || selected.name;
          if (lexName) {
            try {
              const lex = await fetchLexDk(lexName);
              if (lex.lexUrl) data.lexUrl = lex.lexUrl;
              if (!data.wikiExtract && lex.wikiExtract) data.wikiExtract = lex.wikiExtract;
              if (!data.image && lex.image) { data.image = lex.image; data.imageCredit = lex.imageCredit; data.imageLicense = lex.imageLicense; }
            } catch { /* silent */ }
          }
        }
        // Trap Danmark for DK barrows and megaliths — rich archaeological descriptions
        // Use Wikidata label (actual site name) rather than generic type like "Rundhøj"
        if (selected.country === "DK" && (selectedType === "barrow" || selectedType === "megalith")) {
          const GENERIC_BARROW = /^(rundhøj|langhøj|gravrøse|gravhøj|gravhög|röse|gravfält|jættestue|dysse|stendysse|stordysse|langdysse)$/i;
          const trapName = data.wikidataLabel || selected.name;
          if (trapName && !GENERIC_BARROW.test(trapName)) {
            try {
              const trap = await fetchTrapDk(trapName);
              if (trap.lexUrl) data.lexUrl = trap.lexUrl;
              if (trap.wikiExtract && (!data.wikiExtract || trap.wikiExtract.length > (data.wikiExtract?.length ?? 0))) data.wikiExtract = trap.wikiExtract;
              if (trap.heritageDesignation) data.heritageDesignation = trap.heritageDesignation;
              if (trap.municipality) data.municipality = trap.municipality;
              if (!data.image && trap.image) { data.image = trap.image; data.imageCredit = trap.imageCredit; data.imageLicense = trap.imageLicense; }
              if (trap.images) data.images = [...(data.images ?? []), ...trap.images];
            } catch { /* silent */ }
          }
        }
        // Wikipedia article titled with this item's own name, never the type.
        // This is the branch the wrecks come through, and a ship's name is very
        // often also a flower, a woman or a Dutch town. The coordinate gate in
        // the fetcher is what stops the article about the flower.
        if (!data.image && selected.name) {
          try {
            const fallback = await fetchWikipediaTitleImage(selected.name, selected.country, selected.lat, selected.lng);
            if (fallback.image && isRelevantImageUrl(fallback.image)) data.image = fallback.image;
          } catch { /* silent */ }
        }
        // Narrative template fallback for rock art — type-based descriptions
        if (selectedType === "rockart" && selected.type) {
          try {
            const narratives = await loadSiteNarratives();
            const siteType = selected.type;
            const tmpl = narratives[siteType];
            if (tmpl) {
              const langKey = lang as string;
              data.narrativeTemplate = tmpl[langKey] ?? tmpl.en ?? tmpl.da;
            }
          } catch { /* silent */ }
        }
        // Atmospheric art
        try {
          const artData = await loadHeritageArt();
          const art = getAtmosphericArt(artData, selectedType, selected.country);
          if (art) data.atmosphericArt = art;
        } catch { /* silent */ }
        // Europeana items for shipwreck/rockart/industrial — pass type + period + layerType
        try {
          const europeana = await fetchEuropeana(selected.name, selected.country, selected.type, selected.lat, selected.lng, selected.period, selectedType);
          if (europeana.length > 0) { data.europeanaItems = europeana; data.europeanaTotal = (europeana as EuropeanaResults)._total; data.europeanaSearchUrl = (europeana as EuropeanaResults)._searchUrl; }
        } catch { /* silent */ }
        // Museum finds: notable items from this location
        try {
          const finds = await fetchMuseumCollections(selected.lat, selected.lng, selected.country, selected.name, lang);
          if (finds && finds.length > 0) data.museumFinds = finds;
        } catch { /* silent */ }
        // Knowledge graph: Wikidata relations for megalith
        if (selected.wikidataId) {
          try {
            const rels = await fetchWikidataRelations(selected.wikidataId, lang);
            if (rels.length > 0) data.wikidataRelations = rels;
          } catch { /* silent */ }
        }
        // Cross-match: find nearby folklore records within 500m (barrow/megalith)
        if (["barrow", "megalith"].includes(selectedType) && folkloreIndex.current.size > 0) {
          const matches: { id: string; title: string; distance: number }[] = [];
          folkloreIndex.current.forEach(f => {
            const dist = haversineKm(selected.lat, selected.lng, f.lat, f.lng);
            if (dist <= 0.5) matches.push({ id: f.id, title: f.title || f.name, distance: dist });
          });
          if (matches.length > 0) {
            matches.sort((a, b) => a.distance - b.distance);
            data.relatedFolklore = matches.slice(0, 5);
          }
        }
        // Already merged by its own handler; awaited here only so that a chain
        // which beats the deadline still returns a complete result.
        if (swedishDetail) await swedishDetail;
        return data;
      };
      runRaced(acc, enrich, selected.id);
    } else if (selectedType === "plaque") {
      setEnrichLoading(true);
      const acc: Partial<EnrichmentData> = {};
      const enrich = async (): Promise<Partial<EnrichmentData>> => {
        const data = acc;
        const plaque = selected as PlaqueSite;
        // 1. Person enrichment via subject:wikidata
        if (plaque.subjectWikidataId) {
          try {
            const wd = await fetchWikidataImageById(plaque.subjectWikidataId, lang);
            if (wd.image) { data.personImage = wd.image; data.personImageCredit = wd.imageCredit; }
            if (wd.wikiExtract) data.personExtract = wd.wikiExtract;
            if (wd.wikidataLabel) data.personLabel = wd.wikidataLabel;
            // Try Wikipedia directly for longer person extract
            if (wd.wikiExtract) {
              const personWikiUrl = `https://www.wikidata.org/wiki/${plaque.subjectWikidataId}`;
              data.personWikipediaUrl = personWikiUrl;
            }
          } catch { /* silent */ }
          // Get person's Wikipedia URL via Wikidata sitelinks
          try {
            const slRes = await fetch(`https://www.wikidata.org/w/api.php?action=wbgetentities&ids=${plaque.subjectWikidataId}&props=sitelinks&format=json&origin=*`, { signal: AbortSignal.timeout(8000) });
            if (slRes.ok) {
              const slData = await slRes.json();
              const sitelinks = slData.entities?.[plaque.subjectWikidataId]?.sitelinks;
              const personWiki = sitelinks?.[lang + "wiki"]?.title || sitelinks?.dawiki?.title || sitelinks?.nowiki?.title || sitelinks?.svwiki?.title || sitelinks?.enwiki?.title;
              const personWikiLang = sitelinks?.[lang + "wiki"] ? lang : sitelinks?.dawiki ? "da" : sitelinks?.nowiki ? "no" : sitelinks?.svwiki ? "sv" : sitelinks?.enwiki ? "en" : null;
              if (personWiki && personWikiLang) {
                data.personWikipediaUrl = `https://${personWikiLang}.wikipedia.org/wiki/${encodeURIComponent(personWiki)}`;
                // Fetch longer extract from Wikipedia directly
                try {
                  const wiki = await fetchWikipediaDirectly(personWikiLang, personWiki);
                  if (wiki.wikiExtract && (!data.personExtract || wiki.wikiExtract.length > data.personExtract.length)) data.personExtract = wiki.wikiExtract;
                  if (wiki.image && !data.personImage) { data.personImage = wiki.image; data.personImageCredit = wiki.imageCredit; }
                  if (wiki.images) data.images = wiki.images;
                } catch { /* silent */ }
              }
            }
          } catch { /* silent */ }
        } else if (plaque.subjectWikipediaUrl) {
          // Fallback: subject:wikipedia tag
          try {
            const match = plaque.subjectWikipediaUrl.match(/https?:\/\/([a-z]{2,3})\.wikipedia\.org\/wiki\/(.+)/);
            if (match) {
              const wiki = await fetchWikipediaDirectly(match[1], decodeURIComponent(match[2]));
              if (wiki.image) { data.personImage = wiki.image; data.personImageCredit = wiki.imageCredit; }
              if (wiki.wikiExtract) data.personExtract = wiki.wikiExtract;
              if (wiki.images) data.images = wiki.images;
              data.personWikipediaUrl = plaque.subjectWikipediaUrl;
            }
          } catch { /* silent */ }
        }
        // NO place enrichment for plaques (no Lex.dk, Lokalhistoriewiki, coordinate search).
        // These always match nearby buildings/churches, not the plaque's subject.
        // The inscription + person biography (via subject:wikidata) are the core content.
        // Europeana Entity API — fallback for person portrait/biography
        if (plaque.commemorated && !data.personImage) {
          try {
            const entity = await fetchEuropeanaEntity(plaque.commemorated);
            if (entity?.portrait) data.personImage = entity.portrait;
            if (entity?.biography && !data.personExtract) data.personExtract = entity.biography;
          } catch { /* silent */ }
        }
        // Europeana
        try {
          const europeana = await fetchEuropeana(plaque.commemorated || selected.name, selected.country, "memorial plaque", selected.lat, selected.lng, undefined, "plaque");
          if (europeana.length > 0) { data.europeanaItems = europeana; data.europeanaTotal = (europeana as EuropeanaResults)._total; data.europeanaSearchUrl = (europeana as EuropeanaResults)._searchUrl; }
        } catch { /* silent */ }
        // Knowledge graph
        const graphQid = plaque.subjectWikidataId || plaque.wikidataId;
        if (graphQid) {
          try {
            const rels = await fetchWikidataRelations(graphQid, lang);
            if (rels.length > 0) data.wikidataRelations = rels;
          } catch { /* silent */ }
        }
        return data;
      };
      runRaced(acc, enrich, selected.id);
    } else if (selectedType === "stolperstein") {
      setEnrichLoading(true);
      const acc: Partial<EnrichmentData> = {};
      const enrich = async (): Promise<Partial<EnrichmentData>> => {
        const data = acc;
        const stone = selected as StolpersteinSite;
        // 1. Person enrichment via wikidataId
        if (stone.wikidataId) {
          try {
            const wd = await fetchWikidataImageById(stone.wikidataId, lang, stone.country);
            if (wd.image) { data.personImage = wd.image; data.personImageCredit = wd.imageCredit; }
            if (wd.wikiExtract) data.personExtract = wd.wikiExtract;
            if (wd.wikidataLabel) data.personLabel = wd.wikidataLabel;
          } catch { /* silent */ }
          // Get person's Wikipedia URL via Wikidata sitelinks
          try {
            const slRes = await fetch(`https://www.wikidata.org/w/api.php?action=wbgetentities&ids=${stone.wikidataId}&props=sitelinks&format=json&origin=*`, { signal: AbortSignal.timeout(8000) });
            if (slRes.ok) {
              const slData = await slRes.json();
              const sitelinks = slData.entities?.[stone.wikidataId]?.sitelinks;
              const personWiki = sitelinks?.[lang + "wiki"]?.title || sitelinks?.dawiki?.title || sitelinks?.nowiki?.title || sitelinks?.svwiki?.title || sitelinks?.enwiki?.title || sitelinks?.dewiki?.title;
              const personWikiLang = sitelinks?.[lang + "wiki"] ? lang : sitelinks?.dawiki ? "da" : sitelinks?.nowiki ? "no" : sitelinks?.svwiki ? "sv" : sitelinks?.enwiki ? "en" : sitelinks?.dewiki ? "de" : null;
              if (personWiki && personWikiLang) {
                data.personWikipediaUrl = `https://${personWikiLang}.wikipedia.org/wiki/${encodeURIComponent(personWiki)}`;
                try {
                  const wiki = await fetchWikipediaDirectly(personWikiLang, personWiki);
                  if (wiki.wikiExtract && (!data.personExtract || wiki.wikiExtract.length > data.personExtract.length)) data.personExtract = wiki.wikiExtract;
                  if (wiki.image && !data.personImage) { data.personImage = wiki.image; data.personImageCredit = wiki.imageCredit; }
                  if (wiki.images) data.images = wiki.images;
                } catch { /* silent */ }
              }
            }
          } catch { /* silent */ }
        } else if (stone.wikipediaUrl) {
          try {
            const match = stone.wikipediaUrl.match(/https?:\/\/([a-z]{2,3})\.wikipedia\.org\/wiki\/(.+)/);
            if (match) {
              const wiki = await fetchWikipediaDirectly(match[1], decodeURIComponent(match[2]));
              if (wiki.image) { data.personImage = wiki.image; data.personImageCredit = wiki.imageCredit; }
              if (wiki.wikiExtract) data.personExtract = wiki.wikiExtract;
              if (wiki.images) data.images = wiki.images;
              data.personWikipediaUrl = stone.wikipediaUrl;
            }
          } catch { /* silent */ }
        }
        // Europeana
        try {
          const europeana = await fetchEuropeana(selected.name, selected.country, "stolperstein", selected.lat, selected.lng, undefined, "stolperstein");
          if (europeana.length > 0) { data.europeanaItems = europeana; data.europeanaTotal = (europeana as EuropeanaResults)._total; data.europeanaSearchUrl = (europeana as EuropeanaResults)._searchUrl; }
        } catch { /* silent */ }
        // Knowledge graph
        if (stone.wikidataId) {
          try {
            const rels = await fetchWikidataRelations(stone.wikidataId, lang);
            if (rels.length > 0) data.wikidataRelations = rels;
          } catch { /* silent */ }
        }
        return data;
      };
      runRaced(acc, enrich, selected.id);
    } else if (selectedType === "museum") {
      setEnrichLoading(true);
      const acc: Partial<EnrichmentData> = {};
      const enrich = async (): Promise<Partial<EnrichmentData>> => {
        const data = acc;
        const mus = selected as Museum;
        // Museum already has wikidataId — fetch Wikidata image + Wikipedia extract.
        // Assigned into the accumulator rather than over it: the deadline
        // publishes whatever `acc` holds, and rebinding the local `data` would
        // leave it holding an empty object.
        if (mus.wikidataId) {
          Object.assign(data, await fetchWikidataImageById(mus.wikidataId, lang, mus.country));
        }
        const bestMusWikiUrl = mus.wikipediaUrls?.[lang] || mus.wikipediaUrls?.en || mus.wikipediaUrl;
        if (!data.wikiExtract && bestMusWikiUrl) {
          const match = bestMusWikiUrl.match(/https?:\/\/([a-z]{2,3})\.wikipedia\.org\/wiki\/(.+)/);
          if (match) {
            const direct = await fetchWikipediaDirectly(match[1], decodeURIComponent(match[2]));
            if (!data.image && direct.image) data.image = direct.image;
            if (!data.wikiExtract && direct.wikiExtract) data.wikiExtract = direct.wikiExtract;
          }
        }
        // Use pre-fetched image from Wikidata if available
        if (!data.image && mus.image) data.image = mus.image;
        // Commons fallback
        if (!data.image) {
          try {
            const commons = await fetchCommonsNearby(mus.lat, mus.lng, 300, mus.name);
            if (commons.length > 0) {
              data.image = commons[0].url;
              data.imageCredit = commons[0].credit;
              data.imageLicense = commons[0].license;
              data.images = [...(data.images ?? []), ...commons];
            }
          } catch { /* silent */ }
        }
        // Museum collection highlights
        try {
          const highlights = mus.country === "DK" ? await fetchSMKCollection(mus.name) : await fetchDigitaltMuseum(mus.name);
          if (highlights.length > 0) data.collectionHighlights = highlights;
        } catch { /* silent */ }
        // Europeana items for museums
        try {
          const europeana = await fetchEuropeana(mus.name, mus.country, undefined, mus.lat, mus.lng, undefined, "museum");
          if (europeana.length > 0) { data.europeanaItems = europeana; data.europeanaTotal = (europeana as EuropeanaResults)._total; data.europeanaSearchUrl = (europeana as EuropeanaResults)._searchUrl; }
        } catch { /* silent */ }
        // Knowledge graph: Wikidata relations for museum
        if (mus.wikidataId) {
          try {
            const rels = await fetchWikidataRelations(mus.wikidataId, lang);
            if (rels.length > 0) data.wikidataRelations = rels;
          } catch { /* silent */ }
        }
        return data;
      };
      runRaced(acc, enrich, selected.id);
    } else if (selectedType === "unesco") {
      setEnrichLoading(true);
      const acc: Partial<EnrichmentData> = {};
      const enrich = async (): Promise<Partial<EnrichmentData>> => {
        const data = acc;
        const site = selected as UnescoSite;
        // UNESCO sites have good descriptions and images already — try Wikipedia for more
        if (site.image) data.image = site.image;
        // Try Wikidata/Wikipedia for richer extract
        try {
          const wd = await fetchWikidataHeritage(site.lat, site.lng, site.name, undefined, lang);
          if (!data.image && wd.image) data.image = wd.image;
          if (wd.wikiExtract) data.wikiExtract = wd.wikiExtract;
          // The URL travels with the text: without it ForeignWikiNotice cannot say
          // which Wikipedia the text is from, and the panel has no link to it.
          if (wd.wikiExtract && wd.wikipediaUrl) data.wikipediaUrl = wd.wikipediaUrl;
          if (wd.wikidataLabel) data.wikidataLabel = wd.wikidataLabel;
        } catch { /* silent */ }
        // Commons fallback
        if (!data.image) {
          try {
            const commons = await fetchCommonsNearby(site.lat, site.lng, 300, site.name);
            if (commons.length > 0) {
              data.image = commons[0].url;
              data.imageCredit = commons[0].credit;
              data.imageLicense = commons[0].license;
              data.images = [...(data.images ?? []), ...commons];
            }
          } catch { /* silent */ }
        }
        // Europeana items for UNESCO
        try {
          // Use primary name (before comma) for better Europeana relevance
          const euroQuery = site.name.includes(",") ? site.name.split(",")[0].trim() : site.name;
          const europeana = await fetchEuropeana(euroQuery, site.country, undefined, site.lat, site.lng, undefined, "unesco");
          if (europeana.length > 0) { data.europeanaItems = europeana; data.europeanaTotal = (europeana as EuropeanaResults)._total; data.europeanaSearchUrl = (europeana as EuropeanaResults)._searchUrl; }
        } catch { /* silent */ }
        return data;
      };
      runRaced(acc, enrich, selected.id);
    }
    return () => { cancelled = true; };
    // Folklore selections are arrays, so depend on the array-aware id —
    // `selected?.id` alone is undefined for arrays and the effect would never
    // re-run between two folklore pins (pin B kept showing pin A's data).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(selected) ? selected[0]?.id : selected?.id, selectedType]);

  return { enrichment, enrichLoading, setEnrichment, setEnrichLoading, cacheEnrichment };
}
